import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Clock, Users, MapPin } from 'lucide-react';
const serviceData: Record<string, any> = {
  'full-service-events': {
    title: 'Full-Service Events & Weddings',
    tag: 'Celebrations',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=2000',
    description: 'From intimate private parties to grand weddings and community events, we provide full-service catering tailored to your vision. We partner with event planners and community organizations like Women Who Care Ministries to deliver a fusion of diverse cuisines, focusing on cultural inclusivity and unforgettable experiences.',
    features: ['Custom Menu Development based on preferences', 'Fusion of Diverse Cuisines', 'Full-Service Staffing & Setup', 'Partnerships with Event Planners', 'Special Dietary Options Available'],
    stats: [{
      label: 'Event Types',
      value: 'Weddings & Socials',
      icon: Users
    }, {
      label: 'Scale',
      value: 'Events of all sizes',
      icon: Clock
    }, {
      label: 'Style',
      value: 'Custom & Inclusive',
      icon: MapPin
    }]
  },
  'corporate-drop-off': {
    title: 'Corporate & Drop-Off Catering',
    tag: 'Business',
    image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&q=80&w=2000',
    description: 'Impress your clients and energize your team with our professional corporate catering. Whether you need a seamless drop-off service for a luncheon or full-service catering for a large conference, we bring a touch of cultural background to your business events.',
    features: ['Drop-Off & Full-Service Options', 'Catering for Meetings & Conferences', 'Customized Corporate Menus', 'Timely Delivery & Setup', 'Vegan, Gluten-Free & Halal Options'],
    stats: [{
      label: 'Clientele',
      value: 'Corporate & B2B',
      icon: Users
    }, {
      label: 'Service',
      value: 'Drop-off or Full',
      icon: Clock
    }, {
      label: 'Availability',
      value: 'DMV Area',
      icon: MapPin
    }]
  },
  'meal-prep': {
    title: 'Meal Prep & Subscriptions',
    tag: 'Wellness',
    image: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?auto=format&fit=crop&q=80&w=2000',
    description: 'Designed for individuals who do not have the luxury to prepare healthy meals due to their busy schedules. Our meal prep and subscription services offer a fusion of authentic flavors with special attention to dietary preferences, ensuring you eat well without the hassle.',
    features: ['Weekly Subscription Services', 'Special Dietary Options (Vegan, GF, Halal)', 'Healthy & Balanced Portions', 'Fusion of Diverse Cuisines', 'Convenient Doorstep Delivery'],
    stats: [{
      label: 'Target',
      value: 'Busy Individuals',
      icon: Users
    }, {
      label: 'Options',
      value: 'Vegan, GF, Halal',
      icon: Clock
    }, {
      label: 'Frequency',
      value: 'Weekly Subs',
      icon: MapPin
    }]
  }
};
export function ServiceDetail({
  openQuote
}: {
  openQuote?: (type?: string) => void;
}) {
  const {
    id
  } = useParams();
  const service = serviceData[id || ''];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  if (!service) return <div className="py-40 text-center" data-id="element-435">Service not found.</div>;
  return <div className="bg-white min-h-screen" data-id="element-436">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh] w-full overflow-hidden" data-id="element-437">
        <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-438" />
        <div className="absolute inset-0 bg-brand-black/50" data-id="element-439" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4 md:px-6" data-id="element-440">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-441">
            <span className="text-brand-gold font-bold tracking-[0.2em] md:tracking-[0.3em] uppercase text-[10px] md:text-xs mb-4 block" data-id="element-442">
              {service.tag}
            </span>
            <h1 className="text-white text-4xl sm:text-5xl md:text-7xl font-serif" data-id="element-443">
              {service.title}
            </h1>
          </motion.div>
        </div>
        <Link to="/" className="absolute top-24 md:top-32 left-4 md:left-8 text-white flex items-center gap-2 hover:text-brand-gold transition-colors z-10 text-sm md:text-base" data-id="element-444">
          <ArrowLeft size={20} data-id="element-445" /> Back to Home
        </Link>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 md:px-6" data-id="element-446">
        <div className="grid lg:grid-cols-3 gap-12 md:gap-16" data-id="element-447">
          <div className="lg:col-span-2" data-id="element-448">
            <h2 className="text-2xl md:text-3xl font-serif mb-6 md:mb-8" data-id="element-449">
              Excellence in Every Detail
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 md:mb-12" data-id="element-450">
              {service.description}
            </p>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-451">
              {service.features.map((feature: string) => <div key={feature} className="flex gap-4 items-start" data-id="element-452">
                  <CheckCircle2 className="text-brand-gold shrink-0" size={24} data-id="element-453" />
                  <span className="text-gray-700 font-medium" data-id="element-454">{feature}</span>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-1" data-id="element-455">
            <div className="bg-gray-50 p-8 rounded-sm border border-gray-100 sticky top-32" data-id="element-456">
              <h3 className="text-xl font-serif mb-8 border-b border-gray-200 pb-4" data-id="element-457">
                Service Overview
              </h3>
              <div className="space-y-8" data-id="element-458">
                {service.stats.map((stat: any) => <div key={stat.label} className="flex gap-4" data-id="element-459">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-brand-gold" data-id="element-460">
                      <stat.icon size={20} data-id="element-461" />
                    </div>
                    <div data-id="element-462">
                      <p className="text-xs text-gray-400 uppercase tracking-widest mb-1" data-id="element-463">
                        {stat.label}
                      </p>
                      <p className="font-bold text-brand-black" data-id="element-464">{stat.value}</p>
                    </div>
                  </div>)}
              </div>
              <button onClick={() => openQuote && openQuote(service.title)} className="w-full bg-brand-gold text-white py-4 rounded-sm font-bold uppercase tracking-widest mt-12 hover:bg-brand-black transition-all" data-id="element-465">
                Request a Quote
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}